import axios from "axios";
import { api_url } from "../config/config";
import financeV2Adapters from "../adapters/financeV2Adapters";
import { DELAI_DEPOT_MS } from "../helpers/depotTools";

const BASE = `${api_url}financement`;

const financeApiService = {
  // ── Routes de calcul stateless ──────────────────────────────────

  calculateEnvelop: (devisUuids) =>
    axios.post(`${BASE}/enveloppe`, { devis_uuids: devisUuids }),

  initializeDevis: (devisUuid) =>
    axios.post(`${BASE}/devis/initialiser`, { devis_uuid: devisUuid }),

  recalculerPeriodes: (payload) =>
    axios.post(`${BASE}/devis/recalculer`, payload),

  ajouterPeriode: (payload) =>
    axios.post(`${BASE}/devis/ajouter-periode`, payload),

  supprimerPeriode: (payload) =>
    axios.post(`${BASE}/devis/supprimer-periode`, payload),

  // LS-612 — saisie manuelle de la mensualité réelle reçue du financeur pour
  // un palier, indépendante du verrouillage classique (dès Accord reçu).
  saisirMensualiteRetenue: (demandeUuid, devisUuid, numero, payload) =>
    axios.post(
      `${BASE}/demandes/${demandeUuid}/devis/${devisUuid}/periodes/${numero}/mensualite-retenue`,
      payload,
    ),

  // LS-716 — n° de contrat attribué par le financeur (champ libre, dès Accord
  // reçu, ressaisie possible).
  saisirNumeroContrat: (demandeUuid, numeroContrat) =>
    axios.post(`${BASE}/demandes/${demandeUuid}/numero-contrat`, {
      numero_contrat: numeroContrat,
    }),

  // LS-717 — compte comptable du dossier (champ libre, dès Accord reçu,
  // ressaisie possible).
  saisirCompteComptable: (demandeUuid, compteComptable) =>
    axios.post(`${BASE}/demandes/${demandeUuid}/compte-comptable`, {
      compte_comptable: compteComptable,
    }),

  // LS-642 — tableau d'amortissement mensuel d'un devis (affichage du modal).
  getEcheancierMensuel: (demandeUuid, devisUuid) =>
    axios.get(`${BASE}/demandes/${demandeUuid}/devis/${devisUuid}/echeancier`),

  // LS-642 — export xlsx du même tableau (mêmes lignes, sans "Mensualité calculée").
  exporterEcheancierMensuel: (demandeUuid, devisUuid) =>
    axios({
      url: `${BASE}/demandes/${demandeUuid}/devis/${devisUuid}/echeancier/export`,
      method: "GET",
      responseType: "blob",
    }),

  validerEnveloppe: (financeurUuid, enveloppeTotal) =>
    axios.post(`${BASE}/financeur/valider-enveloppe`, {
      financeur_uuid: financeurUuid,
      enveloppe_total: enveloppeTotal,
    }),

  // ── Routes CRUD — Demandes ──────────────────────────────────────

  getDemande: (uuid) => axios.get(`${BASE}/demandes/${uuid}`),

  // Anti-doublon : on interroge la liste filtrée par devis. La réponse est
  // paginée ({ data: [...], meta }). Si data contient une demande, le devis
  // est déjà engagé — sinon le front retombe en mode création.
  getDemandeByDevis: (devisUuid) =>
    axios.get(`${BASE}/demandes?devis_uuid=${devisUuid}`),

  listerDemandes: (search = "") => axios.get(`${BASE}/demandes${search}`),

  // LS-628 — export xlsx de la liste des demandes, mêmes filtres que listerDemandes,
  // sans pagination.
  exporterDemandes: (search = "") =>
    axios({
      url: `${BASE}/demandes/export${search}`,
      method: "GET",
      responseType: "blob",
    }),

  createDemande: (data) => axios.post(`${BASE}/demandes`, data),

  updateDemande: (uuid, data) => axios.put(`${BASE}/demandes/${uuid}`, data),

  // ── Cycle de vie — transitions de statut (7 statuts, 204) ────────

  envoyerDemande: (uuid) => axios.post(`${BASE}/demandes/${uuid}/envoyer`),

  // LS-625 — envoi (ou renvoi) par email au financeur, distinct de la transition
  // ci-dessus : passe la demande à "Demande envoyée" au premier envoi uniquement,
  // un renvoi ne re-transitionne pas.
  envoyerAuFinanceur: (uuid, payload) =>
    axios.post(`${BASE}/demandes/${uuid}/envoyer-au-financeur`, payload),

  accordRecuDemande: (uuid) =>
    axios.post(`${BASE}/demandes/${uuid}/accord-recu`),

  dossierCompletDemande: (uuid) =>
    axios.post(`${BASE}/demandes/${uuid}/dossier-complet`),

  dossierTransmisDemande: (uuid) =>
    axios.post(`${BASE}/demandes/${uuid}/dossier-transmis`),

  transmisEtSigneDemande: (uuid) =>
    axios.post(`${BASE}/demandes/${uuid}/transmis-et-signe`),

  // LS-629 — abandon depuis n'importe quel statut, réservé ADMIN/SUPER_ADMIN.
  abandonnerDemande: (uuid) =>
    axios.post(`${BASE}/demandes/${uuid}/abandonner`),

  // ── Consommation d'enveloppe (dashboard) ────────────────────────

  getConsommationEnveloppe: (financeurUuid, search = "") =>
    axios.get(`${BASE}/financeurs/${financeurUuid}/enveloppe${search}`),

  // ── Enveloppes financeur (N enveloppes — CRUD) ──────────────────
  // index renvoie { consommation: <cumul financeur>, enveloppes: [{…, consommation}] }

  listerEnveloppes: (financeurUuid) =>
    axios.get(`${BASE}/financeurs/${financeurUuid}/enveloppes`),

  creerEnveloppe: (financeurUuid, payload) =>
    axios.post(`${BASE}/financeurs/${financeurUuid}/enveloppes`, payload),

  modifierEnveloppe: (financeurUuid, enveloppeUuid, payload) =>
    axios.put(
      `${BASE}/financeurs/${financeurUuid}/enveloppes/${enveloppeUuid}`,
      payload,
    ),

  cloturerEnveloppe: (financeurUuid, enveloppeUuid) =>
    axios.post(
      `${BASE}/financeurs/${financeurUuid}/enveloppes/${enveloppeUuid}/cloture`,
    ),

  modifierEtapesEnveloppe: (financeurUuid, enveloppeUuid, payload) =>
    axios.patch(
      `${BASE}/financeurs/${financeurUuid}/enveloppes/${enveloppeUuid}/etapes`,
      payload,
    ),

  synchroniserTodosEnveloppe: (financeurUuid, enveloppeUuid, payload) =>
    axios.put(
      `${BASE}/financeurs/${financeurUuid}/enveloppes/${enveloppeUuid}/todos`,
      payload,
    ),

  // ── Gabarit financeur (étapes/todos par défaut, hérités par ses
  // enveloppes tant qu'elles ne les surchargent pas) ──────────────────

  modifierEtapesFinanceur: (financeurUuid, payload) =>
    axios.patch(`${BASE}/financeurs/${financeurUuid}/etapes`, payload),

  synchroniserTodosFinanceur: (financeurUuid, payload) =>
    axios.put(`${BASE}/financeurs/${financeurUuid}/todos`, payload),

  // ── Todos — demande (checklist de l'étape courante) ─────────────

  basculerTodoDemande: (demandeUuid, todoUuid, coche) =>
    axios.patch(`${BASE}/demandes/${demandeUuid}/todos/${todoUuid}`, {
      coche,
    }),

  // ── Encours Crédit-Bail (LS-623) ────────────────────────────────
  // Une ligne par véhicule financé des demandes Transmis et signé, avec
  // paliers, mensualité courante et montant à échoir ventilé par véhicule.

  listerEncours: (search = "") => axios.get(`${BASE}/encours${search}`),

  exporterEncours: (search = "") =>
    axios({
      url: `${BASE}/encours/export${search}`,
      method: "GET",
      responseType: "blob",
    }),

  // ── Soldes anticipés par véhicule (LS-722) ──────────────────────
  // Suivi 100 % manuel (décision du 10/09/2026) : demande envoyée au financeur
  // → facture pro forma reçue → soldé/payé. Chaque réponse renvoie le suivi
  // complet { immatriculations_disponibles, soldes }.

  listerSoldes: (demandeUuid) =>
    axios.get(`${BASE}/demandes/${demandeUuid}/soldes`),

  enregistrerDemandeDeSolde: (demandeUuid, immatriculations, dateDemande) =>
    axios.post(`${BASE}/demandes/${demandeUuid}/soldes`, {
      immatriculations,
      date_demande: dateDemande,
    }),

  mettreAJourSolde: (demandeUuid, immatriculation, payload) =>
    axios.put(
      `${BASE}/demandes/${demandeUuid}/soldes/${immatriculation}`,
      financeV2Adapters.toApi.mettreAJourSolde(payload),
    ),

  annulerSolde: (demandeUuid, immatriculation) =>
    axios.delete(`${BASE}/demandes/${demandeUuid}/soldes/${immatriculation}`),

  // ── Documents — tableau récapitulatif ───────────────────────────

  uploaderRecap: (uuid, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(
      `${BASE}/demandes/${uuid}/documents/tableau_recapitulatif`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  },

  getRecap: (uuid) =>
    axios.get(`${BASE}/demandes/${uuid}/documents/tableau_recapitulatif`),

  // ── Documents — dépôt libre (qualifiés par catégorie TypeDocument) ──

  // `devisUuid` — requis par l'API pour le type `tableau_amortissement_source`
  // (LS-634, rattachement fichier ↔ devis) ; ignoré pour les autres types.
  uploaderDocument: (uuid, file, type = "autre", devisUuid = null) => {
    const formData = new FormData();
    formData.append("file", file);
    if (devisUuid) formData.append("devis_uuid", devisUuid);
    // ST1-2163 — timeout explicite : sans lui, un stockage en panne laissait
    // l'utilisateur attendre sans retour (cf. helpers/depotTools).
    return axios.post(`${BASE}/demandes/${uuid}/documents/${type}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: DELAI_DEPOT_MS,
    });
  },

  getDocuments: (uuid, type = "autre") =>
    axios.get(`${BASE}/demandes/${uuid}/documents/${type}`),

  // LS-649 — suppression définitive d'un document déjà uploadé.
  supprimerDocument: (uuid, type, mediaUuid) =>
    axios.delete(`${BASE}/demandes/${uuid}/documents/${type}/${mediaUuid}`),

  // ── Accord de financement BPCE (LS-657 / LS-668) ─────────────────

  // LS-689 — `tamponId` : tampon choisi parmi ceux de l'utilisateur connecté,
  // apposé sur le PDF. Jamais implicite — omis (ou null), le PDF est généré sans
  // tampon, même si l'utilisateur en a déposé.
  genererAccord: (uuid, fournisseurId, tamponId = null) =>
    axios.post(`${BASE}/demandes/${uuid}/generer-accord`, {
      fournisseur_id: fournisseurId,
      tampon_id: tamponId || null,
    }),

  // LS-687 — destinataire/cc/commentaire/fournisseurId (carnet d'adresses), aligné sur
  // envoyerAuFinanceur.
  envoyerAccordFournisseur: (uuid, payload) =>
    axios.post(
      `${BASE}/demandes/${uuid}/envoyer-accord-fournisseur`,
      financeV2Adapters.toApi.envoyerAccordFournisseur(payload),
    ),
};

export default financeApiService;
